import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import Banner from "@/components/ui/banner";
import { AlertBubbleWarning } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import { formatDateShort } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";

interface Props {
  isOpen: boolean;
  isSaving: boolean;
  /** Effective date of the amendment this correction will cancel. */
  effectiveDate: string;
  onKeepEditing: () => void;
  onConfirm: () => void;
}

// Saving a correction withdraws any scheduled amendment, so the user meets that
// consequence while they can still back out. Deliberately at save rather than in
// the kind picker: dismissing has to leave the edited set intact, which only
// exists once the editor is open.
const CancelAmendmentWarningDialog: React.FC<Props> = ({
  isOpen,
  isSaving,
  effectiveDate,
  onKeepEditing,
  onConfirm
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={isOpen}
      setIsOpen={(open) => !open && onKeepEditing()}
      contentClassname="w-[35rem]"
      data-test="cancel-amendment-warning"
      title={
        <Text as="div" variant="lgSubtitleH1">
          {t("contracts.updateTerms.cancelAmendment.title")}
        </Text>
      }
      content={
        <div className="flex flex-col gap-4 p-4">
          {/* The shared Banner rather than a hand-rolled block: SaveTermsModal
              next door builds this by hand, but that copy predates Banner's
              warning variant and has since drifted from it (no border, a
              different icon). One file over, UpdateTermsModal already uses
              Banner for its info notice. */}
          <Banner variant="warning" className="p-2.5">
            <div className="flex items-start gap-2">
              <IconContainer size="sm" className="text-warning shrink-0">
                <AlertBubbleWarning />
              </IconContainer>
              <Text
                variant="smBodyWarning"
                data-test="cancel-amendment-message"
              >
                {t("contracts.updateTerms.cancelAmendment.message", {
                  date: formatDateShort(effectiveDate)
                })}
              </Text>
            </div>
          </Banner>
          <Text variant="body">
            {t("contracts.updateTerms.cancelAmendment.prompt")}
          </Text>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondaryNoBg"
            onClick={onKeepEditing}
            data-test="cancel-amendment-keep-editing"
          >
            {t("contracts.updateTerms.cancelAmendment.keepEditing")}
          </Button>
          <Button
            disabled={isSaving}
            onClick={onConfirm}
            data-test="cancel-amendment-confirm"
          >
            {t("contracts.updateTerms.cancelAmendment.confirm")}
          </Button>
        </div>
      }
      hideDivider
    />
  );
};

export default CancelAmendmentWarningDialog;
